import { useEffect, useMemo, useState } from "react";
import { MoreHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { displayTitle } from "./displayTitle.js";
import {
  Plus,
  Search,
  Edit,
  Trash,
  Arrow,
  Briefcase,
} from "../../components/icons.js";
import { Modal } from "../admin/adminUi.js";
import { budgetLabel, reqStageBadge } from "./pipeline.js";
import { STATE_RANK, FUNNEL_STAGES, type FunnelStageKey } from "./funnelStages.js";
import {
  getRequisitionStatus,
  getRequisitionDepartment,
  getRequisitionExperience,
  getRequisitionLocation,
  getRequisitionSkills,
  getSourcingType,
  type RequisitionStatusCategory,
} from "./requisitionFormat.js";

interface RequisitionRow {
  id: string;
  title: string;
  state: string;
  region?: string;
  job_description?: string | null;
  verticalKey?: string;
  config?: {
    entryMode?: string;
    sourcing?: boolean;
    verticalKey?: string;
    department?: string;
    targetExperience?: string;
    location?: string;
    budgetMin?: number | string;
    budgetMax?: number | string;
    salaryMin?: number;
    salaryMax?: number;
    salaryCurrency?: string;
    skills?: string[];
    competencies?: string[];
    levels?: string[];
  };
  candidateCount?: number;
  appliedCount?: number;
  matchesCount?: number;
  scannedCount?: number;
  interviewCount?: number;
  /** Candidacies per state (list query) — the same numbers the requirement page's funnel counts. */
  stageCounts?: Record<string, number>;
  questionCount?: number;
  lastAppliedAt?: string;
  lastActivityAt?: string;
  industryKey?: string | null;
  roleFamilyKey?: string | null;
  specialisationKeys?: string[];
  createdAt?: string;
  /** #235: who created the requirement (GET /requisitions). */
  createdByName?: string;
}

interface RequisitionsResponse {
  requisitions: RequisitionRow[];
}


/** What each index stage counts — same meaning as the requirement page. */
const STAGE_HINT: Partial<Record<FunnelStageKey, string>> = {
  uploaded: "Candidates and resumes received",
  applied: "Candidates who applied",
  invited: "Sent an interview invitation",
  scheduled: "Interview scheduled",
  interviewed: "Completed the interview",
  finalists: "Shortlisted for final consideration",
};

type ReqSort = "newest" | "activity" | "lastApplied" | "applied" | "oldest" | "az" | "za";

const SORT_KEY = "th:req-sort";

/** Requirement list orderings. "Last updated" and "Latest application" come from the list
 *  endpoint's derived lastActivityAt / lastAppliedAt (a requirement with no candidates sorts last). */
const REQ_SORTS: { key: ReqSort; label: string }[] = [
  { key: "newest", label: "Newest first" },
  { key: "activity", label: "Last updated" },
  { key: "lastApplied", label: "Latest application" },
  { key: "applied", label: "Most applied" },
  { key: "oldest", label: "Oldest first" },
  { key: "az", label: "Title A–Z" },
  { key: "za", label: "Title Z–A" },
];

const ms = (iso?: string | null): number => (iso ? Date.parse(iso) : 0);

function sorterFor(k: ReqSort): (a: RequisitionRow, b: RequisitionRow) => number {
  const title = (r: RequisitionRow): string => displayTitle(r.title).toLowerCase();
  switch (k) {
    case "activity":
      return (a, b) => ms(b.lastActivityAt ?? b.createdAt) - ms(a.lastActivityAt ?? a.createdAt);
    case "lastApplied":
      return (a, b) => ms(b.lastAppliedAt) - ms(a.lastAppliedAt) || ms(b.createdAt) - ms(a.createdAt);
    case "applied":
      return (a, b) => (b.appliedCount ?? 0) - (a.appliedCount ?? 0) || ms(b.createdAt) - ms(a.createdAt);
    case "oldest":
      return (a, b) => ms(a.createdAt) - ms(b.createdAt);
    case "az":
      return (a, b) => title(a).localeCompare(title(b));
    case "za":
      return (a, b) => title(b).localeCompare(title(a));
    default:
      return (a, b) => ms(b.createdAt) - ms(a.createdAt);
  }
}

export function Requisitions(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | RequisitionStatusCategory>("Active");
  const [sortBy, setSortBy] = useState<ReqSort>(() => {
    try {
      const saved = localStorage.getItem(SORT_KEY);
      return REQ_SORTS.some((o) => o.key === saved) ? (saved as ReqSort) : "newest";
    } catch {
      return "newest";
    }
  });
  const chooseSort = (k: ReqSort): void => {
    setSortBy(k);
    try {
      localStorage.setItem(SORT_KEY, k);
    } catch {
      /* the choice just isn't remembered */
    }
  };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Which row's "…" menu is open (delete lives there, behind the existing confirm — audit X10).
  const [menuFor, setMenuFor] = useState<string | null>(null);
  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent): void => {
      if (!(e.target as HTMLElement).closest?.(".t1r-menu-wrap")) setMenuFor(null);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuFor(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [menuFor]);


  // Delete flow state. `blocked` holds the API's refusal so the page can explain it instead of
  // quietly doing something else — see the deleteMutation comment.
  const [confirmDelete, setConfirmDelete] = useState<RequisitionRow | null>(null);
  const [blocked, setBlocked] = useState<
    { req: RequisitionRow; message: string; canClose: boolean } | null
  >(null);
  const [actionNote, setActionNote] = useState<string | null>(null);

  // Fetch live requisitions
  const { data, isLoading } = useQuery({
    queryKey: ["requisitions"],
    queryFn: () => api.get<RequisitionsResponse>("/requisitions"),
    refetchOnMount: "always",
  });

  // The list is what the API returned. It used to fall back to three invented requisitions when
  // the org had none, which showed a brand-new customer a populated pipeline for roles that did
  // not exist — with working-looking Edit and Delete buttons on rows with no database row behind
  // them. An empty org gets the empty state below instead.
  const requisitionsList = data?.requisitions ?? [];

  // Set default selected id
  const activeSelectedId = selectedId || requisitionsList[0]?.id || "";

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    void queryClient.invalidateQueries({ queryKey: ["org-stats"] });
  };

  /**
   * DELETE, honestly.
   *
   * This used to `try { del } catch { close }` — so when the API refused the delete (it refuses
   * any requisition that has candidates, resume scans or billed items; see requisitionsDelete in
   * apps/api/src/functions/requisitions.ts) the page silently CLOSED the requisition instead and
   * reported success. The user pressed Delete, was told it worked, and the job stayed on the
   * list in a different state. Two different outcomes behind one button, with no way to tell
   * which one you got.
   *
   * Now the refusal is surfaced: the API already sends back the counts and a readable reason, so
   * the dialog shows them and offers Close as its own, separately-chosen action.
   */
  const deleteMutation = useMutation({
    mutationFn: (req: RequisitionRow) => api.del(`/requisitions/${req.id}`),
    onSuccess: (_res, req) => {
      setConfirmDelete(null);
      setActionNote(`"${displayTitle(req.title)}" was deleted.`);
      invalidate();
    },
    onError: (err, req) => {
      setConfirmDelete(null);
      // 409 requisition_not_empty — the API says exactly what is in the way and whether Close is
      // still available. Anything else is a genuine failure and says so rather than guessing.
      const body =
        err instanceof ApiError && err.status === 409
          ? (err.body as { message?: string; canClose?: boolean } | undefined)
          : undefined;
      if (body?.message) {
        setBlocked({ req, message: body.message, canClose: body.canClose !== false });
        return;
      }
      setActionNote(
        err instanceof ApiError && err.status === 403
          ? "Only an org super admin, or whoever raised this request, can delete it."
          : "Couldn't delete that requirement. Please retry in a moment.",
      );
    },
  });

  const closeMutation = useMutation({
    mutationFn: (req: RequisitionRow) =>
      api.post(`/requisitions/${req.id}/close`, { reason: "Closed from the Job Requirements list" }),
    onSuccess: (_res, req) => {
      setBlocked(null);
      setActionNote(
        `"${displayTitle(req.title)}" is closed. It's off your active list and everything on it stays auditable.`,
      );
      invalidate();
    },
    onError: () => setActionNote("Couldn't close that requirement. Please retry in a moment."),
  });

  // Filter requisitions
  const filteredRequisitions = useMemo(() => {
    return requisitionsList.filter((r) => {
      // Status Filter
      if (statusFilter !== "All") {
        const cat = getRequisitionStatus(r.state);
        if (cat !== statusFilter) return false;
      }

      // Search Filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = r.title.toLowerCase().includes(q);
        const deptMatch = getRequisitionDepartment(r).toLowerCase().includes(q);
        const skillMatch = getRequisitionSkills(r).some((s) => s.toLowerCase().includes(q));
        if (!titleMatch && !deptMatch && !skillMatch) return false;
      }

      return true;
    }).sort(sorterFor(sortBy));
  }, [requisitionsList, statusFilter, searchQuery, sortBy]);

  const activePositionsCount = useMemo(() => {
    return requisitionsList.filter((r) => getRequisitionStatus(r.state) === "Active").length;
  }, [requisitionsList]);


  return (
    <div className="dashboard-container t1r">
      {/* Page header (X1) — the top bar already carries "New requirement" */}
      <header className="page-h t1r-head">
        <div>
          <h1>Job requirements</h1>
          <div className="sub">
            {activePositionsCount} active · {requisitionsList.length - activePositionsCount} draft, on hold or closed. Open one
            to edit its rubric, or view the candidates attached to it.
          </div>
        </div>
      </header>

      {/* Search & Filter Toolbar */}
      <div className="t1r-toolbar">
        <label className="t1r-search">
          <Search aria-hidden="true" />
          <input
            type="search"
            placeholder="Search by title, skill or department"
            aria-label="Search requirements by title, skills, or department"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>

        <label className="t1r-sort">
          <span>Sort</span>
          <select value={sortBy} onChange={(e) => chooseSort(e.target.value as ReqSort)} aria-label="Sort requirements">
            {REQ_SORTS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </label>

        <div className="seg t1r-seg" role="group" aria-label="Filter by status">
          {(["All", "Active", "Draft", "Hold", "Closed"] as const).map((st) => (
            <button
              key={st}
              type="button"
              className={statusFilter === st ? "on" : ""}
              aria-pressed={statusFilter === st}
              onClick={() => setStatusFilter(st)}
            >
              {st === "Hold" ? "On hold" : st}
            </button>
          ))}
        </div>
      </div>

      {/* Requisitions List */}
      <div className="t1r-list">
        {filteredRequisitions.map((req) => {
          const isSelected = req.id === activeSelectedId;
          const stage = reqStageBadge(req.state);
          const sourcingType = getSourcingType(req);
          const dept = getRequisitionDepartment(req);
          const exp = getRequisitionExperience(req);
          const loc = getRequisitionLocation(req);
          const budget = budgetLabel(req);
          const skills = getRequisitionSkills(req);
          const title = displayTitle(req.title);

          // The requirement page's stages, counted the same way (cumulative: everyone who reached
          // a stage or went past it), so the index and the detail page never disagree.
          const counts = req.stageCounts ?? {};
          const reached = (rank: number): number =>
            Object.entries(counts).reduce(
              (a, [state, n]) => a + ((STATE_RANK[state as keyof typeof STATE_RANK] ?? 0) >= rank ? n : 0),
              0,
            );
          const stageKeys: FunnelStageKey[] = [
            req.config?.entryMode === "post" ? "applied" : "uploaded",
            "invited",
            "scheduled",
            "interviewed",
            "finalists",
          ];
          const menuOpen = menuFor === req.id;

          return (
            <div
              key={req.id}
              className={`t1r-row${isSelected ? " is-selected" : ""}`}
              // #235: the whole card opens the requirement (the title link stays for keyboard and
              // new-tab use; the actions area stops the click from reaching here).
              onClick={() => {
                setSelectedId(req.id);
                if (window.getSelection()?.toString()) return; // selecting text is not a click
                navigate(`/app/requisitions/${req.id}`);
              }}
            >
              <div className="t1r-main">
                <div className="t1r-title-row">
                  <Link to={`/app/requisitions/${req.id}`} className="t1r-title" title={title}>
                    {title}
                  </Link>
                  <span className={stage.cls}>{stage.label}</span>
                  <span className={sourcingType.cls} title={sourcingType.hint}>{sourcingType.label}</span>
                </div>
                <div className="t1r-meta">
                  <span>{dept}</span>
                  <span aria-hidden="true">·</span>
                  <span>{exp}</span>
                  <span aria-hidden="true">·</span>
                  <span>{loc}</span>
                  <span aria-hidden="true">·</span>
                  <span className={budget ? "t1r-budget" : undefined}>{budget ?? "Budget not set"}</span>
                </div>
                {req.createdAt && (
                  <div className="t1r-posted">
                    <span>
                      Posted{" "}
                      <time dateTime={req.createdAt}>
                        {new Date(req.createdAt).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </time>
                    </span>
                    {req.createdByName && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>
                          by <b>{req.createdByName}</b>
                        </span>
                      </>
                    )}
                  </div>
                )}
                {skills.length > 0 && (
                  <div className="t1r-skills">
                    {skills.slice(0, 3).map((skill) => (
                      <span key={skill} className="t1r-chip">{skill}</span>
                    ))}
                    {skills.length > 3 && (
                      <span className="t1r-chip more" title={skills.slice(3).join(", ")}>+{skills.length - 3}</span>
                    )}
                  </div>
                )}
              </div>

              <dl className="t1r-stats" aria-label="Candidate funnel">
                {stageKeys.map((key) => (
                  <div key={key} title={STAGE_HINT[key]}>
                    <dt>{FUNNEL_STAGES[key].label}</dt>
                    <dd>{reached(FUNNEL_STAGES[key].rank)}</dd>
                  </div>
                ))}
              </dl>

              <div className="t1r-actions" onClick={(e) => e.stopPropagation()}>
                <Link to={`/app/candidates?reqId=${req.id}`} className="btn sm t1r-view">
                  View candidates <Arrow />
                </Link>
                <div className="t1r-menu-wrap">
                  <button
                    type="button"
                    className="t1r-more"
                    aria-label={`More actions for ${title}`}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    data-tip={menuOpen ? undefined : "More actions"}
                    data-tip-pos="left"
                    onClick={() => setMenuFor(menuOpen ? null : req.id)}
                  >
                    <MoreHorizontal size={18} />
                  </button>
                  {menuOpen && (
                    <div className="t1r-menu" role="menu" onMouseLeave={() => setMenuFor(null)}>
                      <Link role="menuitem" to={`/app/requisitions/${req.id}`} onClick={() => setMenuFor(null)}>
                        <Edit /> Edit requirement
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        className="is-danger"
                        onClick={() => {
                          setMenuFor(null);
                          setConfirmDelete(req);
                        }}
                      >
                        <Trash /> Delete…
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Don't render an empty state over a request that is still in flight — a confident
            "you have no requirements" that turns into a list a moment later reads as data loss. */}
        {isLoading && requisitionsList.length === 0 && (
          <div className="card t1r-empty">
            <p>Loading your requirements…</p>
          </div>
        )}

        {/* Two different situations, two different messages. "Nothing matches this filter" is a
            dead end when the real answer is "you haven't created one yet". */}
        {!isLoading && filteredRequisitions.length === 0 && (
          <div className="card t1r-empty">
            {requisitionsList.length === 0 ? (
              <>
                <span className="t1r-empty-ico"><Briefcase /></span>
                <h3>No job requirements yet</h3>
                <p>
                  Create your first requirement and TalentHop will screen resumes against it, run
                  voice AI interviews, and rank candidates for you.
                </p>
              </>
            ) : (
              <p>No requirements match “{statusFilter === "Hold" ? "On hold" : statusFilter}”{searchQuery.trim() ? " and your search" : ""}.</p>
            )}
            <Link to="/app/new" className="btn primary">
              <Plus />
              <span>{requisitionsList.length === 0 ? "Create your first requirement" : "New requirement"}</span>
            </Link>
          </div>
        )}
      </div>

      {/* ── delete: ask first, and say what is actually about to happen ─────── */}
      {confirmDelete && (
        <Modal
          title={`Delete "${displayTitle(confirmDelete.title)}"?`}
          onClose={() => setConfirmDelete(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={() => setConfirmDelete(null)}>
                Keep it
              </button>
              <button
                className="btn danger"
                onClick={() => deleteMutation.mutate(confirmDelete)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          }
        >
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            The requirement and its interview questions are removed for good.
            <div style={{ marginTop: 10 }}>
              If anyone has been added to this role, or anything on it has been billed, the delete
              is refused and you'll be offered Close instead — nothing with candidates or a
              billing trail behind it can be erased by a single click.
            </div>
          </div>
        </Modal>
      )}

      {/* ── the refusal, shown rather than silently worked around ───────────── */}
      {blocked && (
        <Modal
          title="This requirement can't be deleted"
          onClose={() => setBlocked(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={() => setBlocked(null)}>
                Leave it open
              </button>
              {blocked.canClose && (
                <button
                  className="btn primary"
                  onClick={() => closeMutation.mutate(blocked.req)}
                  disabled={closeMutation.isPending}
                >
                  {closeMutation.isPending ? "Closing…" : "Close it instead"}
                </button>
              )}
            </div>
          }
        >
          {/* The API's own sentence, with its counts — not a paraphrase that could drift out of
              step with the rule the server actually applies. */}
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            {blocked.message}
            <div style={{ marginTop: 10 }}>
              To remove it anyway, delete its candidates first from{" "}
              <Link to={`/app/candidates?reqId=${blocked.req.id}`}>Candidates</Link>, then delete
              the requirement.
            </div>
          </div>
        </Modal>
      )}

      {actionNote && (
        <Modal title="Done" onClose={() => setActionNote(null)}>
          <div className="mut sm">{actionNote}</div>
        </Modal>
      )}
    </div>
  );
}
